import React, { useState, useMemo } from "react";
import {
  Autocomplete,
  TextField,
  Checkbox,
  FormControl,
  FormLabel,
  Chip,
  Box,
  Typography,
  FormHelperText,
  IconButton,
} from "@mui/material";
import CheckBoxOutlineBlank from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBox from "@mui/icons-material/CheckBox";
import { UpperIcon } from "../../assets/common-svg/CommonSvg";

const icon = <CheckBoxOutlineBlank fontSize="small" />;
const checkedIcon = <CheckBox fontSize="small" />;

const SearchableDropdown = ({
  options = [],
  value,
  onChange,
  onBlur,
  placeholder = "Select...",
  label,
  multiple = false,
  required = false,
  disabled = false,
  error = false,
  helperText = "",
  ...props
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  const safeOptions = Array.isArray(options) ? options : [];

  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return safeOptions;

    const searchLower = searchTerm.toLowerCase().trim();
    return safeOptions.filter((option) => {
      const name = option?.name || option?.label || "";
      return name.toLowerCase().includes(searchLower);
    });
  }, [safeOptions, searchTerm]);

  const handleEvent = (eventType, event, value) => {
    switch (eventType) {
      case "change":
        onChange(value);
        break;
      case "blur":
        onBlur?.(event);
        break;
      case "inputChange":
        setSearchTerm(value);
        break;
      case "toggleDropdown":
        setIsOpen(value !== undefined ? value : !isOpen);
        break;
      default:
        break;
    }
  };

  const CustomIconComponent = (props) => (
    <IconButton
      {...props}
      sx={{
        padding: "0.25rem",
        "&:hover": { backgroundColor: "transparent" },
      }}
    >
      <div
        className={`cursor-pointer transition-transform duration-200 ${
          isOpen ? "rotate-180" : ""
        }`}
        style={{ margin: "0.1rem" }}
      >
        <UpperIcon />
      </div>
    </IconButton>
  );

  const getSafeValue = () => {
    if (multiple) return Array.isArray(value) ? value : [];
    return value || null;
  };

  return (
    <FormControl
      fullWidth
      sx={{
        mb: "1rem",
        position: "relative",
      }}
    >
      {/* Label */}
      {label && (
        <FormLabel
          sx={{
            mb: "0.4rem",
            color: "text.primary",
            fontWeight: 500,
            fontSize: "0.9rem",
            lineHeight: "1.1rem",
          }}
        >
          {label}
          {required && (
            <Typography
              component="span"
              sx={{ color: "error.main", ml: "0.2rem" }}
            >
              *
            </Typography>
          )}
        </FormLabel>
      )}

      {/* Autocomplete */}
      <Autocomplete
        multiple={multiple}
        disableCloseOnSelect={multiple}
        options={filteredOptions}
        value={getSafeValue()}
        onChange={(event, newValue) => handleEvent("change", event, newValue)}
        onBlur={(event) => handleEvent("blur", event)}
        onInputChange={(event, newInputValue) =>
          handleEvent("inputChange", event, newInputValue)
        }
        onOpen={() => handleEvent("toggleDropdown", null, true)}
        onClose={() => handleEvent("toggleDropdown", null, false)}
        getOptionLabel={(option) => option?.name || option?.label || ""}
        isOptionEqualToValue={(option, value) => option?.id === value?.id}
        disabled={disabled}
        popupIcon={<CustomIconComponent />}
        renderTags={(selected, getTagProps) =>
          selected.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option?.id}
              label={option?.name || option?.label}
              size="small"
              sx={{
                borderRadius: "0.25rem",
                fontSize: "0.75rem",
                height: "1.5rem",
                backgroundColor: "#F5F5F5",
                border: "0.0625rem solid #E0E0E0",
                "& .MuiChip-deleteIcon": {
                  fontSize: "1rem",
                },
              }}
            />
          ))
        }
        renderInput={(params) => (
         <TextField
  {...params}
  placeholder={placeholder}
  size="small"
  error={error}
  sx={{
    "& .MuiOutlinedInput-root": {
      backgroundColor: disabled ? "#F5F5F5" : "#FFFFFF",
      borderRadius: "0.375rem",
      border: "0.0625rem solid #E0E0E0",
      minHeight: "3.00rem", 
      fontFamily: "Roboto, sans-serif",
      fontSize: "0.875rem",
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap", 
      "&:hover": {
        borderColor: "#BDBDBD",
      },
      "&.Mui-focused": {
        borderColor: "#1976D2",
        boxShadow: "0 0 0 0.1rem rgba(25, 118, 210, 0.25)",
      },
      "& fieldset": {
        border: "none",
      },
    },
    "& .MuiInputBase-input": {
      fontSize: "0.9rem",
      padding: "0.5rem 0.75rem",
      color: disabled ? "#9E9E9E" : "#000000",
      textTransform: "capitalize",
    },
    "& .MuiInputBase-input::placeholder": {
      color: "#777777",
      opacity: 1,
    },
  }}
/>

        )}
        renderOption={(props, option, { selected }) => (
          <Box
            component="li"
            {...props}
            key={option.id}
            sx={{
              display: "flex",
              alignItems: "center",
              fontSize: "0.875rem",
              py: "0.5rem",
              px: "1rem",
              borderBottom: "0.0625rem solid #F0F0F0",
              "&:last-child": { borderBottom: "none" },
              "&:hover": { backgroundColor: "#F5F5F5" },
            }}
          >
            {multiple && (
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                checked={selected}
                sx={{
                  mr: "0.75rem",
                  color: "#BDBDBD",
                  "&.Mui-checked": { color: "#1976D2" },
                }}
              />
            )}
            <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
              {option?.name || option?.label}
            </Typography>
          </Box>
        )}
        ListboxProps={{
          sx: {
            maxHeight: "15.625rem",
            overflowY: "auto",
            "&::-webkit-scrollbar": {
              width: "0.375rem",
            },
            "&::-webkit-scrollbar-track": {
              background: "#F5F5F5",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#BDBDBD",
              borderRadius: "0.1875rem",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "#9E9E9E",
            },
          },
        }}
        sx={{
          "& .MuiAutocomplete-endAdornment": {
            right: "0.5rem",
          },
        }}
        {...props}
      />

      {/* Helper Text */}
      {helperText && (
        <FormHelperText
          error={error}
          sx={{
            position: "absolute",
            left: 0,
            bottom: "-1.5rem",
            mx: 0,
            mt: "0.25rem",
            fontSize: "0.75rem",
            lineHeight: "1.0625rem",
          }}
        >
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default SearchableDropdown;
